/**
 * SlotMachine3D Wrapper for Deals Page
 *
 * Integrates the new 3D slot machine with the deals page modal system.
 */
/* eslint-disable rumio-i18n/no-hardcoded-strings */

import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../../../stores/auth-store';
import { getAccessToken } from '../../../../lib/auth';
import { SlotMachine3D, type SpinResult } from './slot-machine/SlotMachine3D';
import {
  useSlotMachineV2,
  type SpinResultV2,
  type HoldAndSpinGridPosition,
} from './slot-machine/hooks/useSlotMachineV2';
import { rumioDefaultTheme } from './slot-machine/themes/rumio-default/theme.config';
import { HoldAndSpinBonus } from './slot-machine/components/UI/HoldAndSpinBonus';
import { FreeSpinsOverlay } from './slot-machine/components/UI/FreeSpinsOverlay';
import { RumioJackpotCelebration } from './slot-machine/components/UI/RumioJackpotCelebration';
import type { SymbolId } from './slot-machine/themes/types';
import { useRumioBalance } from '../hooks/useRumios';

interface SlotMachine3DWrapperProps {
  /** Called when login is required */
  onLoginRequired?: () => void;
}

/**
 * Convert SpinResultV2 (from API) to SpinResult (for 3D component)
 */
function convertToComponentSpinResult(apiResult: SpinResultV2): SpinResult {
  return {
    grid: apiResult.grid,
    wins: apiResult.wins.map((w) => ({
      lineNumber: w.lineNumber,
      symbolId: w.symbolId as SymbolId,
      matchCount: w.matchCount,
      positions: w.positions,
      winAmount: w.winAmount,
    })),
    totalWin: apiResult.totalWin,
    freeSpinsTriggered: apiResult.features.freeSpinsTriggered || undefined,
    bonusTriggered: apiResult.features.bonusTriggered,
  };
}

export function SlotMachine3DWrapper({ onLoginRequired }: SlotMachine3DWrapperProps) {
  const { user } = useAuthStore();
  const [spinResult, setSpinResult] = useState<SpinResult | null>(null);
  const [showJackpotCelebration, setShowJackpotCelebration] = useState(false);
  const [jackpotInfo, setJackpotInfo] = useState<{
    payout: number;
    months: number;
    row: number | null;
  } | null>(null);
  const [cachedToken, setCachedToken] = useState<string | null>(null);

  // Keep auth token cached for sync access
  useEffect(() => {
    getAccessToken().then(setCachedToken);
  }, [user]);

  // Get auth token (sync for slot machine hook)
  const getAuthToken = useCallback(() => {
    return cachedToken;
  }, [cachedToken]);

  // Fetch balance from the main Rumios API (same as hero section)
  const { data: rumioBalance } = useRumioBalance();
  const actualBalance = rumioBalance?.balance ?? 0;

  // Check if user is logged in
  if (!user && onLoginRequired) {
    onLoginRequired();
  }

  // Initialize the slot machine hook
  const {
    isLoading,
    isSpinning,
    error,
    status,
    betPerLine,
    activeLines,
    totalBet,
    isFreeSpinMode,
    freeSpinsRemaining,
    freeSpinMultiplier,
    holdAndSpinState,
    setBetPerLine,
    setActiveLines,
    spin,
    spinFreeSpin,
    spinHoldAndSpin,
    closeHoldAndSpin,
    clearError,
  } = useSlotMachineV2({
    getAuthToken,
    onSpinComplete: (result) => {
      setSpinResult(convertToComponentSpinResult(result));
    },
    onWin: (result) => {
      console.log('Win!', result.totalWin);
    },
    onBigWin: (result) => {
      console.log('BIG WIN!!!', result.totalWin);
    },
    onFreeSpinsTriggered: (spins, multiplier) => {
      console.log(`Free spins triggered! ${spins} spins at ${multiplier}x`);
    },
    onBonusTriggered: () => {
      console.log('Hold & Spin bonus triggered!');
    },
    onJackpot: (prize) => {
      console.log('JACKPOT!!!', prize);
      // Convert prize format
      const months = parseInt(prize.value.replace(/\D/g, ''), 10) || 1;
      setJackpotInfo({
        payout: 50 * totalBet, // 50x total bet
        months,
        row: null,
      });
      setShowJackpotCelebration(true);
    },
  });

  // Handle spin button click
  const handleSpin = useCallback(async () => {
    if (!user) {
      onLoginRequired?.();
      return;
    }

    if (isFreeSpinMode) {
      await spinFreeSpin();
    } else if (status?.canDailySpin) {
      await spin('free_daily');
    } else {
      await spin('paid');
    }
  }, [user, isFreeSpinMode, status?.canDailySpin, spin, spinFreeSpin, onLoginRequired]);

  // Handle Hold & Spin bonus spin
  const handleHoldAndSpinSpin = useCallback(async () => {
    const result = await spinHoldAndSpin();
    if (result) {
      return {
        grid: result.grid.map((pos: HoldAndSpinGridPosition) => ({
          hasRing: pos.hasRing,
          value: pos.value,
        })),
        spinsRemaining: result.spinsRemaining,
        totalValue: result.totalValue,
        newRings: result.newRings,
        isComplete: result.isComplete,
        isGrandBonus: result.isGrandBonus,
        payout: result.payout,
      };
    }
    return null;
  }, [spinHoldAndSpin]);

  // Calculate total free spins won (for display)
  const totalFreeSpinsWon = status?.activeFreeSpins
    ? freeSpinsRemaining + (status.activeFreeSpins.spinsRemaining - freeSpinsRemaining)
    : freeSpinsRemaining;

  if (isLoading && !status) {
    return (
      <div className="flex items-center justify-center h-96 bg-gradient-to-b from-slate-900 to-slate-800 rounded-xl">
        <div className="text-white text-lg">Loading slot machine...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Error display */}
      {error && (
        <div className="bg-red-500/20 border border-red-500 text-red-400 p-4 rounded-lg flex justify-between items-center">
          <span>{error}</span>
          <button onClick={clearError} className="text-red-400 hover:text-red-300">
            Dismiss
          </button>
        </div>
      )}

      {/* Free Spins Overlay */}
      {isFreeSpinMode && (
        <FreeSpinsOverlay
          isActive={isFreeSpinMode}
          isSpinning={isSpinning}
          totalSpins={totalFreeSpinsWon}
          spinsRemaining={freeSpinsRemaining}
          multiplier={freeSpinMultiplier}
          totalWon={0}
        />
      )}

      {/* Hold & Spin Bonus Game */}
      {holdAndSpinState && (
        <HoldAndSpinBonus
          isActive={true}
          initialGrid={holdAndSpinState.grid.map((pos: HoldAndSpinGridPosition) => ({
            hasRing: pos.hasRing,
            value: pos.value,
          }))}
          initialSpins={holdAndSpinState.spinsRemaining}
          betPerLine={betPerLine}
          onSpin={handleHoldAndSpinSpin}
          onComplete={(payout, isGrandBonus) => {
            console.log(`Hold & Spin complete! Payout: ${payout}, Grand: ${isGrandBonus}`);
            closeHoldAndSpin();
          }}
          onClose={closeHoldAndSpin}
        />
      )}

      {/* RUMIO Jackpot Celebration */}
      {showJackpotCelebration && (
        <RumioJackpotCelebration
          isActive={showJackpotCelebration}
          jackpot={jackpotInfo}
          onClose={() => setShowJackpotCelebration(false)}
        />
      )}

      {/* Main Slot Machine */}
      {!holdAndSpinState && (
        <SlotMachine3D
          theme={rumioDefaultTheme}
          isSpinning={isSpinning}
          spinResult={spinResult}
          onSpin={handleSpin}
          betPerLine={betPerLine}
          activeLines={activeLines}
          balance={actualBalance}
          className="rounded-xl overflow-hidden"
        />
      )}

      {/* Betting Controls */}
      {!isFreeSpinMode && !holdAndSpinState && (
        <div className="bg-slate-800 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Bet per line */}
            <div>
              <label htmlFor="bet-per-line-value" className="text-gray-400 text-sm block mb-2">
                Bet per Line
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setBetPerLine(betPerLine - 1)}
                  disabled={betPerLine <= 1}
                  className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
                >
                  -
                </button>
                <span id="bet-per-line-value" className="text-white font-bold min-w-[3rem] text-center">
                  {betPerLine}
                </span>
                <button
                  onClick={() => setBetPerLine(betPerLine + 1)}
                  disabled={betPerLine >= 10}
                  className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
                >
                  +
                </button>
              </div>
            </div>

            {/* Active lines */}
            <div>
              <label htmlFor="active-lines-value" className="text-gray-400 text-sm block mb-2">
                Active Lines
              </label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setActiveLines(activeLines - 1)}
                  disabled={activeLines <= 1}
                  className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
                >
                  -
                </button>
                <span id="active-lines-value" className="text-white font-bold min-w-[3rem] text-center">
                  {activeLines}
                </span>
                <button
                  onClick={() => setActiveLines(activeLines + 1)}
                  disabled={activeLines >= 20}
                  className="px-3 py-1 bg-slate-700 text-white rounded disabled:opacity-50"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Total bet summary */}
          <div className="mt-4 pt-4 border-t border-slate-700 flex justify-between items-center">
            <span className="text-gray-400">Total Bet:</span>
            <span className="text-yellow-400 font-bold text-xl">{totalBet} Rumios</span>
          </div>

          {/* Daily spin indicator */}
          {status?.canDailySpin && (
            <div className="mt-2 text-center">
              <span className="text-green-400 text-sm">Daily free spin available!</span>
            </div>
          )}
        </div>
      )}

      {/* Balance display */}
      <div className="bg-slate-800 rounded-lg p-4 flex justify-between items-center">
        <div>
          <div className="text-gray-400 text-sm">Your Balance</div>
          <div className="text-white font-bold text-xl">{actualBalance} Rumios</div>
        </div>
        <div className="text-right">
          <div className="text-gray-400 text-sm">Multiplier</div>
          <div className="text-yellow-400 font-bold text-xl">
            {(status?.engagementMultiplier ?? 1).toFixed(2)}x
          </div>
        </div>
      </div>
    </div>
  );
}

export default SlotMachine3DWrapper;
